import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Smartphone, RotateCcw } from 'lucide-react';
import { SCREEN_PRESETS } from '../game/gameTemplates';

/**
 * Admin wrapper that embeds the game in an iframe with aspect-ratio controls.
 * Route: /play/:teamId
 * Query params forwarded to game: ?entryStoryflow=&entryNode=
 */
export default function PlayPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const entryStoryflow = searchParams.get('entryStoryflow') || '';
  const entryNode = searchParams.get('entryNode') || '';
  const teamId = window.location.pathname.split('/play/')[1]?.split('?')[0] || '';

  const [activePresetId, setActivePresetId] = useState('3:4');

  const activePreset = useMemo(
    () => SCREEN_PRESETS.find((p) => p.id === activePresetId) || SCREEN_PRESETS[0],
    [activePresetId]
  );

  const gameSrc = useMemo(() => {
    const params = new URLSearchParams();
    if (teamId) params.set('teamId', teamId);
    if (entryStoryflow) params.set('entryStoryflow', entryStoryflow);
    if (entryNode) params.set('entryNode', entryNode);
    return `/game?${params.toString()}`;
  }, [teamId, entryStoryflow, entryNode]);

  const [iframeKey, setIframeKey] = useState(0);
  const reload = useCallback(() => setIframeKey((k) => k + 1), []);

  return (
    <div className="flex h-screen w-full flex-col bg-zinc-950 text-zinc-200">
      {/* Toolbar */}
      <div className="flex items-center gap-2 border-b border-zinc-800/90 bg-zinc-900 px-3 py-2 shrink-0">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center justify-center rounded p-1.5 text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 transition-colors"
          title="Back"
        >
          <ArrowLeft size={18} strokeWidth={2} />
        </button>

        {/* Aspect ratio chips — portrait only */}
        <div className="flex items-center gap-1 flex-wrap">
          {SCREEN_PRESETS.map((preset) => {
            const active = preset.id === activePresetId;
            return (
              <button
                key={preset.id}
                type="button"
                onClick={() => setActivePresetId(preset.id)}
                title={`${preset.device} (${preset.label})`}
                className={`
                  flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-colors
                  ${active
                    ? 'bg-violet-500/15 text-violet-400 ring-1 ring-violet-500/30'
                    : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/80'}
                `}
              >
                <Smartphone size={13} strokeWidth={2} />
                <span>{preset.label}</span>
              </button>
            );
          })}
        </div>

        <div className="flex-1" />

        <button
          type="button"
          onClick={reload}
          title="Reload game"
          className="flex items-center justify-center rounded p-1.5 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 transition-colors"
        >
          <RotateCcw size={16} strokeWidth={2} />
        </button>
      </div>

      {/* Game viewport — fills remaining height, no scroll */}
      <div className="flex-1 flex items-center justify-center overflow-hidden bg-black min-h-0">
        <div
          className="relative bg-black overflow-hidden"
          style={{
            aspectRatio: `${activePreset.w} / ${activePreset.h}`,
            height: '100%',
            maxWidth: '100%',
          }}
        >
          <iframe
            key={iframeKey}
            src={gameSrc}
            title="Game"
            className="absolute inset-0 w-full h-full"
            style={{ border: 'none' }}
          />
        </div>
      </div>
    </div>
  );
}
